import React, { useEffect } from "react";
import Nav from "../../components/Nav/Nav";

import {ManagerContext, UserContext} from "../../App";
import { Button, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TextField, Box, Alert, Paper, CircularProgress, Chip, ToggleButton, ToggleButtonGroup, Stack } from "@mui/material";

import './ManagerHomeView.scss';
import { Link, useNavigate } from "react-router-dom";
import { ContextTicketDto } from "../HomeView/HomeView";
import WeekPreview from "../../components/WeekPreview/WeekPreview";
import CourtEventApi, { APPEARANCE_LABELS, CourtEventDto } from "../../api/CourtEventApi";
import { staffTicketUrl } from "../../utils/appearanceAlertUrls";
import CalendarEntryApi, { JuryTrialDateDto } from "../../api/CalendarEntryApi";
import { DateTime } from 'luxon';
import { View, Views } from 'react-big-calendar';
import { deriveStageLabel, isClosedStage } from "../../utils/caseStage";
import {
  UPCOMING_WINDOW_DAYS,
  groupCasesNeedingAction,
} from './casesNeedingAction';
import { clientDisplayName } from "../../utils/clientName";

type PreviewView = 'day' | 'week' | 'month';

const PREVIEW_VIEW_KEY = 'home.courtEventsView';

/** The view last chosen in this browser, or week, which is what the home page always showed. */
const savedPreviewView = (): PreviewView => {
  try {
    const saved = window.localStorage.getItem(PREVIEW_VIEW_KEY);
    return saved === 'day' || saved === 'month' ? saved : 'week';
  } catch {
    return 'week';
  }
};

/**
 * The span of court events to load for a view. Weeks start on Sunday because that is how
 * react-big-calendar draws them; months take a week either side for the spill-over days shown
 * from the neighbouring months.
 */
const previewRange = (date: DateTime, view: PreviewView) => {
  if (view === 'day') return { start: date.startOf('day'), end: date.endOf('day') };
  if (view === 'month') {
    return {
      start: date.startOf('month').minus({ days: 7 }),
      end: date.endOf('month').plus({ days: 7 }),
    };
  }
  const sunday = date.minus({ days: date.weekday % 7 }).startOf('day');
  return { start: sunday, end: sunday.plus({ days: 6 }).endOf('day') };
};

/** The heading over the calendar, e.g. "Monday 26 October", "25 – 31 October 2026", "October 2026". */
const previewLabel = (date: DateTime, view: PreviewView) => {
  if (view === 'day') return date.toFormat('cccc d LLLL yyyy');
  if (view === 'month') return date.toFormat('LLLL yyyy');
  const { start, end } = previewRange(date, 'week');
  return start.month === end.month
    ? `${start.toFormat('d')} – ${end.toFormat('d LLLL yyyy')}`
    : `${start.toFormat('d LLL')} – ${end.toFormat('d LLL yyyy')}`;
};

export const ticketStatusNames: TicketStatusNameType[] = [
  'citation received (Approving)',
  'case accepted (Approved)',
  'letter of representation sent (Active)',
  'court respond by date satisfied (Pending)',
  'New court date',
  'negotiating with prosecutor',
  'offer received',
  'waiting for client to reject or accept offer',
  'waiting for judge to approve',
  'deferral/DSC conditions',
  'case closed/dismissed (closed)'
];
export type TicketStatusNameType = 
  'citation received (Approving)' 
  | 'case accepted (Approved)' 
  | 'letter of representation sent (Active)' 
  | 'court respond by date satisfied (Pending)' 
  | 'New court date' 
  | 'negotiating with prosecutor' 
  | 'offer received' 
  | 'waiting for client to reject or accept offer' 
  | 'waiting for judge to approve'
  | 'deferral/DSC conditions'
  | 'case closed/dismissed (closed)';

// "Cases Needing Action" is decided on the server across every active case
// (backend utils/cases/casesNeedingAction.ts, Lloyd 14 Oct 2026), not from the capped list below.

// Phase 4 (G.14) — stale-case flagging.
// A case is "stale" when it has quietly stalled and would otherwise look like it's just waiting:
//   • no-ack       — the LOR was sent but the court never acknowledged it, past the follow-up window
//   • no-court-date — the LOR was acknowledged but there is no upcoming court date on file
// LOR follow-ups escalate through day 3 / 7 / 10; we treat "past the window" as 10+ days unconfirmed.
const STALE_LOR_UNCONFIRMED_DAYS = 10;
// A case opened this long ago that still has no LOR activity at all is "going cold" — this catches
// older Clio-imported cases that never got LOR flags (Lloyd, Jul 8: cases slipping through). New
// imports now get age-based LOR flags on import, so this mainly surfaces the pre-existing backlog.
const STALE_COLD_NO_ACTIVITY_DAYS = 21;

type StaleReason = 'no-ack' | 'no-court-date' | 'going-cold';

const daysSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
};

const getStaleReason = (ct: ContextTicketDto): StaleReason | null => {
  const t = ct.ticket;

  // Non-traffic matters are never stale by these rules (Lloyd, Aug 6). All three reasons below are
  // about a letter of representation the court never acknowledged, or a court date that follows
  // from one. A criminal matter has no LOR, so every one of them would report "going cold" forever
  // and bury the traffic cases this list exists to surface.
  if (t.Ticket_MatterType === 'NON_TRAFFIC') return null;

  const confirmed = Boolean(t.Ticket_LorConfirmedAt);

  // 1) LOR sent but never acknowledged, past the day-10 follow-up window.
  if (t.Ticket_LorSent && !confirmed) {
    const age = daysSince(t.Ticket_LorSentAt);
    if (age !== null && age >= STALE_LOR_UNCONFIRMED_DAYS) return 'no-ack';
  }

  // 2) Acknowledged, but no upcoming court date scheduled.
  if (confirmed) {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const courtDate = t.Ticket_CourtDate ? new Date(t.Ticket_CourtDate) : null;
    const hasUpcomingCourtDate =
      courtDate !== null &&
      !Number.isNaN(courtDate.getTime()) &&
      courtDate.getTime() >= startOfToday.getTime();
    if (!hasUpcomingCourtDate) return 'no-court-date';
  }

  // 3) Going cold — opened weeks ago but no LOR activity at all. Catches older imported cases that
  // never got LOR flags (so they'd otherwise never appear on the LOR-based stale checks above).
  // Skip closed cases.
  if (!t.Ticket_LorSent && !confirmed && !isClosedStage(t, ct.ticketStatus?.TicketStatus_Name)) {
    const age = daysSince(t.Ticket_CreatedAt);
    if (age !== null && age >= STALE_COLD_NO_ACTIVITY_DAYS) return 'going-cold';
  }

  return null;
};

const STALE_REASON_LABEL: Record<StaleReason, string> = {
  'no-ack': 'No LOR acknowledgment',
  'no-court-date': 'No upcoming court date',
  'going-cold': 'Going cold — no recent activity',
};


/**
 * How many active cases the dashboard pulls into the browser.
 *
 * Not a total. Production holds 5,953 active cases, so anything counted from this list is a count
 * of the page, and the screen says so with a "+" when the cap is reached.
 */
const ACTIVE_TICKETS_PAGE_SIZE = 2000;

const ManagerHomeView = () => {
  const manager = React.useContext(ManagerContext);
  const navigate = useNavigate();

  const [activeTickets, setActiveTickets] = React.useState<ContextTicketDto[]>([]);
  /*
   * Whether that fetch hit its cap, so the counts derived from it can say they are partial.
   *
   * 🔴 The dashboard asks for 2,000 active cases and there are 5,953 on production. Everything below
   * that is computed from the rows in the browser, so "Stale Cases (41)" was never a total — it was
   * however many of the first 2,000 happened to qualify, printed as though it were the answer. The
   * court-silence count beside it goes to the server for `matchedTotal` precisely to avoid this.
   */
  const [activeTicketsCapped, setActiveTicketsCapped] = React.useState(false);
  const [ticketsLoading, setTicketsLoading] = React.useState(true);
  // Server-decided, over every active case (Lloyd, 14 Oct 2026: "1,104 cases needing action").
  const [needsAction, setNeedsAction] = React.useState<ContextTicketDto[]>([]);
  const [needsActionLoading, setNeedsActionLoading] = React.useState(true);
  const [needsActionError, setNeedsActionError] = React.useState(false);
  // Old Clio cases with no stage set: their own list, loaded a page at a time when asked for.
  const [clioTotal, setClioTotal] = React.useState(0);
  const [clioCases, setClioCases] = React.useState<ContextTicketDto[]>([]);
  const [clioLoading, setClioLoading] = React.useState(false);
  const [clientSearch, setClientSearch] = React.useState('');

  const handleClientSearch = () => {
    const term = clientSearch.trim();
    // `name` searches first and last name alike — a first name typed here used to find nothing
    // (Ashley, Aug 18).
    navigate(term ? `/users?name=${encodeURIComponent(term)}` : '/users');
  };

  
  // State for court events
  /**
   * How many cases the court has gone quiet on, from the server planner.
   *
   * Null until it answers, and null again if it fails: the section hides rather than showing a
   * zero, because "no cases waiting on a court" and "we could not ask" look identical as a number
   * and only one of them is good news.
   */
  const [courtSilenceCount, setCourtSilenceCount] = React.useState<number | null>(null);
  const [courtEvents, setCourtEvents] = React.useState<CourtEventDto[]>([]);
  const [eventsLoading, setEventsLoading] = React.useState(true);
  const [eventsError, setEventsError] = React.useState<string | null>(null);
  const [juryTrials, setJuryTrials] = React.useState<JuryTrialDateDto[]>([]);
  const [previewView, setPreviewView] = React.useState<PreviewView>(savedPreviewView);
  const [previewDate, setPreviewDate] = React.useState<DateTime>(() => DateTime.now());

  const handleTicketClick = (ticketGuid: string) => {
    navigate(`/ticket?Ticket_guid=${ticketGuid}`);
  };

  useEffect(() => {
    // Fetch active tickets when the component mounts
    getActiveTickets();
    getNeedsAction();
    // How many cases the court has gone quiet on. Server-side; see below for why.
    getCourtSilenceCount();
  }, []);

  // Court events for the day, week or month on screen, reloaded whenever either changes.
  useEffect(() => {
    let current = true;
    loadPreviewCourtEvents(previewDate, previewView, () => current);
    return () => {
      current = false;
    };
  }, [previewDate, previewView]);

  /** `isCurrent` drops a slow answer that arrives after the user has already moved on. */
  const loadPreviewCourtEvents = async (
    date: DateTime,
    view: PreviewView,
    isCurrent: () => boolean,
  ) => {
    try {
      setEventsLoading(true);
      setEventsError(null);

      const { start, end } = previewRange(date, view);
      const [events, trials] = await Promise.all([
        CourtEventApi.listCourtEvents({
          CourtEvent_Start_From: start.toMillis(),
          CourtEvent_Start_To: end.toMillis()
        }),
        // Trial dates typed on the case. A failure here must not hide the settings, so it is
        // swallowed to an empty list; the Calendar page reports it.
        CalendarEntryApi.juryTrials(start.toISODate()!, end.toISODate()!).catch(() => []),
      ]);

      if (!isCurrent()) return;
      setCourtEvents(events);
      setJuryTrials(trials);
    } catch (err) {
      if (!isCurrent()) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to load court events';
      setEventsError(errorMessage);
      console.error('Error loading court events:', err);
    } finally {
      if (isCurrent()) setEventsLoading(false);
    }
  };

  const changePreviewView = (view: PreviewView) => {
    setPreviewView(view);
    try {
      window.localStorage.setItem(PREVIEW_VIEW_KEY, view);
    } catch {
      // Not remembered this time; the choice still holds for this visit.
    }
  };

  /** Back or forward one day, week or month. */
  const stepPreview = (direction: 1 | -1) => {
    setPreviewDate((date) => date.plus({ [`${previewView}s`]: direction }));
  };

  /** Month view: a click on a date or on "+N more" opens that day. */
  const handlePreviewView = (view: View) => {
    if (view === Views.DAY || view === Views.WEEK || view === Views.MONTH) changePreviewView(view);
  };
  
  const CLIO_PAGE_SIZE = 50;

  /** Cases Needing Action, and the Clio total. `clioFrom` set loads the next page of Clio cases. */
  const fetchNeedsAction = async (clioFrom: number, clioSize: number) => {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/tickets/needing-action?clioFrom=${clioFrom}&clioSize=${clioSize}`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load cases needing action');
    return (await response.json()) as {
      cases: ContextTicketDto[];
      clioToReview: { total: number; cases: ContextTicketDto[] };
    };
  };

  const getNeedsAction = async () => {
    setNeedsActionLoading(true);
    setNeedsActionError(false);
    try {
      const body = await fetchNeedsAction(0, 0);
      setNeedsAction(body.cases);
      setClioTotal(body.clioToReview.total);
    } catch (error) {
      console.error('Error fetching cases needing action:', error);
      setNeedsActionError(true);
    } finally {
      setNeedsActionLoading(false);
    }
  };

  const loadMoreClio = async () => {
    setClioLoading(true);
    try {
      const body = await fetchNeedsAction(clioCases.length, CLIO_PAGE_SIZE);
      setClioCases((current) => [...current, ...body.clioToReview.cases]);
      setClioTotal(body.clioToReview.total);
    } catch (error) {
      console.error('Error fetching Clio cases to review:', error);
    } finally {
      setClioLoading(false);
    }
  };

  const getActiveTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await fetch((process.env.API_HOST||'') + `/api/v1/tickets?size=${ACTIVE_TICKETS_PAGE_SIZE}&Ticket_Active=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }
      const tickets = await response.json();
      setActiveTickets(tickets);
      setActiveTicketsCapped(Array.isArray(tickets) && tickets.length >= ACTIVE_TICKETS_PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setTicketsLoading(false);
    }
  }

  /**
   * The 90-day court-silence count (Lloyd, 2 Oct 2026).
   *
   * Server-side because the rule reads court emails, court-sourced offers and the manual contact
   * stamp — none of which the dashboard's ticket list carries. `months=1` is deliberately the
   * widest bucket: the 6/5/4/3 staging is for Lloyd's one-off catch-up batches, and a standing
   * count should not hide a case for being young when it cannot be 90 days silent unless it is.
   *
   * `matchedTotal`, not the returned rows — the cap on that list is a review limit, and reporting
   * the capped number here would understate the backlog on a dashboard.
   */
  const getCourtSilenceCount = async () => {
    try {
      const response = await fetch(
        (process.env.API_HOST || '') + '/api/v1/manager/stale-cases?months=1&cap=1',
        { credentials: 'include' },
      );
      if (!response.ok) return;
      const body = await response.json();
      if (typeof body?.matchedTotal === 'number') setCourtSilenceCount(body.matchedTotal);
    } catch (error) {
      // Left silent on purpose. This is one card on a dashboard whose other sections still have to
      // render; the stale-cases screen is where the same number is reported properly.
      console.error('Error fetching the court-silence count:', error);
    }
  };


  const grouped = groupCasesNeedingAction(needsAction);

  /**
   * The hearing type, which is what tells staff whether a date is a pre-trial, a show cause or a
   * trial (Raquel, 14 Sep). It lives on the next court event rather than on the case, and plenty of
   * cases have a date with no event behind it, so it falls back rather than showing an empty column.
   */
  const hearingType = (ct: ContextTicketDto): string =>
    ct.nextCourtEvent?.CourtEvent_Desc?.trim() || '—';

  /** One group's table. Empty groups are skipped entirely rather than showing a heading with nothing under it. */
  const renderActionTable = (heading: string, rows: ContextTicketDto[], note: string) => {
    if (rows.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          {heading} ({rows.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {note}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Case #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Hearing</TableCell>
                <TableCell>Court Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((ct) => (
                <TableRow
                  key={ct.ticket.Ticket_guid}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleTicketClick(ct.ticket.Ticket_guid)}
                >
                  <TableCell>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                      {ct.ticket.Ticket_MatterNumber || ct.ticket.Ticket_Number || '\u2014'}
                    </Typography>
                  </TableCell>
                  <TableCell>{ct.user && clientDisplayName(ct.user)}</TableCell>
                  <TableCell>{renderStageChip(ct)}</TableCell>
                  <TableCell>{hearingType(ct)}</TableCell>
                  <TableCell>
                    {ct.ticket.Ticket_CourtDate
                      ? new Date(ct.ticket.Ticket_CourtDate).toLocaleDateString('en-US')
                      : '\u2014'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Render the mapped 8-stage label for a case instead of the raw internal status / legacy
  // "approving" wording, so the dashboard tracks Lloyd's 8 stages (Jul 8 feedback).
  const renderStageChip = (ct: ContextTicketDto) => {
    const statusName = ct.ticketStatus?.TicketStatus_Name;
    const closed = isClosedStage(ct.ticket, statusName);
    const color = closed ? 'default' : !statusName ? 'warning' : 'info';
    return (
      // Same court-date floor the cases list uses. `nextCourtEvent` is future-only, so its presence
      // is the signal; without it this chip disagreed with the cases list about the same case.
      <Chip
        label={deriveStageLabel(ct.ticket, statusName, Boolean(ct.nextCourtEvent))}
        color={color}
        size="small"
      />
    );
  };

  // Stale cases among the loaded active cases (G.14).
  const staleCases = activeTickets
    .map((ct) => ({ ct, reason: getStaleReason(ct) }))
    .filter((x): x is { ct: ContextTicketDto; reason: StaleReason } => x.reason !== null);

  return (
    <>
      <div className="mahomev">
        <Container className="mahomev-ctn">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
            <Typography variant="h3" className="mahomev-ctn-ttl">Welcome {manager?.Manager_FirstName}</Typography>
          </Box>

          {/* Client search */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label="Search clients by name"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleClientSearch(); }}
            />
            <Button variant="contained" onClick={handleClientSearch}>
              Search
            </Button>
          </Paper>

          {/* Court events: day, week or month (Lloyd, 26 Oct 2026). */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              sx={{ gap: 1.5, mb: 2 }}
            >
              <Box>
                <Typography variant="h5">Court Events</Typography>
                <Typography variant="body2" color="text.secondary">
                  {previewLabel(previewDate, previewView)}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => stepPreview(-1)}>
                  Back
                </Button>
                <Button size="small" variant="outlined" onClick={() => setPreviewDate(DateTime.now())}>
                  Today
                </Button>
                <Button size="small" variant="outlined" onClick={() => stepPreview(1)}>
                  Next
                </Button>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={previewView}
                  // Null when the selected button is clicked again; keep the view rather than none.
                  onChange={(_, view: PreviewView | null) => view && changePreviewView(view)}
                >
                  <ToggleButton value="day">Day</ToggleButton>
                  <ToggleButton value="week">Week</ToggleButton>
                  <ToggleButton value="month">Month</ToggleButton>
                </ToggleButtonGroup>
                <Button size="small" component={Link} to="/calendar">
                  Full calendar
                </Button>
              </Stack>
            </Stack>
            {eventsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load court events: {eventsError}
              </Alert>
            ) : (
              // The calendar stays up while loading and when empty, so the controls above it keep
              // their place and an empty week reads as an empty week.
              <Paper elevation={1} sx={{ p: 2, position: 'relative' }}>
                {eventsLoading && (
                  <CircularProgress size={24} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }} />
                )}
                {!eventsLoading && courtEvents.length === 0 && juryTrials.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No court events in this {previewView}.
                  </Typography>
                )}
                <WeekPreview
                  courtEvents={courtEvents}
                  juryTrials={juryTrials}
                  currentDate={previewDate.toJSDate()}
                  view={previewView}
                  onNavigate={(date) => setPreviewDate(DateTime.fromJSDate(date))}
                  onView={handlePreviewView}
                  minShownTime="07:00"
                  maxShownTime="19:00"
                />
                {/*
                  The day as a plain list too (Kelsi: "can there also be a daily format so we can
                  clearly see what's on the calendar for each given day?"). A busy docket is seven
                  settings at 9:00, which the grid can only draw as seven slivers side by side.
                */}
                {previewView === 'day' && !eventsLoading && (juryTrials.length > 0 || courtEvents.length > 0) && (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 110 }}>Time</TableCell>
                          <TableCell>Setting</TableCell>
                          <TableCell>Client appearance</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {juryTrials.map((trial) => (
                          <TableRow key={`trial-${trial.ticketGuid}-${trial.date}`}>
                            <TableCell>All day</TableCell>
                            <TableCell>{trial.title}</TableCell>
                            <TableCell>No time recorded on the case</TableCell>
                            <TableCell align="right">
                              <Button size="small" component={Link} to={staffTicketUrl(trial.ticketGuid)}>
                                Open case
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {[...courtEvents]
                          .sort((a, b) => Number(a.CourtEvent_Start) - Number(b.CourtEvent_Start))
                          .map((event) => (
                            <TableRow key={event.CourtEvent_guid}>
                              <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                {DateTime.fromMillis(Number(event.CourtEvent_Start)).toFormat('h:mm a')}
                              </TableCell>
                              <TableCell>{event.CourtEvent_CalendarLabel || event.CourtEvent_Title || 'Court event'}</TableCell>
                              <TableCell>
                                {event.CourtEvent_IsHearing === false
                                  ? '—'
                                  : APPEARANCE_LABELS[event.CourtEvent_AppearanceRequirement ?? 'NOT_REQUIRED']}
                              </TableCell>
                              <TableCell align="right">
                                {event.Ticket_guid && (
                                  <Button
                                    size="small"
                                    component={Link}
                                    to={staffTicketUrl(event.Ticket_guid, event.CourtEvent_guid)}
                                  >
                                    Open case
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}
          </Box>

          {/* Cases Needing Action */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Cases Needing Action
              {needsActionLoading || needsActionError ? '' : ` (${needsAction.length})`}
            </Typography>
            {needsActionError ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="error">
                  Could not load the cases needing action. Refresh the page to try again.
                </Typography>
              </Paper>
            ) : needsActionLoading ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading cases...
                </Typography>
              </Paper>
            ) : needsAction.length === 0 ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No cases need action right now.
                </Typography>
              </Paper>
            ) : (
              <>
                {/*
                  Three lists, not one (Raquel, 14 Sep 2026). She was shown 1,095 rows in no order,
                  with a hearing from August beside one set for next February. Grouping and ordering
                  live in `casesNeedingAction.ts`, tested; the reason they are grouped rather than
                  filtered to two weeks is in that file's header, and it is that 4,183 active cases
                  are already past their court date and a plain filter would hide every one of them.
                */}
                {renderActionTable('Overdue', grouped.overdue, 'The court date has passed. Newest first.')}
                {renderActionTable(
                  `Next ${UPCOMING_WINDOW_DAYS} days`,
                  grouped.upcoming,
                  'Soonest first.',
                )}
                {renderActionTable(
                  'No court date on file',
                  grouped.undated,
                  'Find out when these are set.',
                )}
                {grouped.laterCount > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {grouped.laterCount} further case(s) need action but are set beyond the next{' '}
                    {UPCOMING_WINDOW_DAYS} days.
                  </Typography>
                )}
              </>
            )}
          </Box>

          {/*
            Old Clio cases that came across with no stage and no LOR on record (Lloyd, 14 Oct 2026).
            They looked exactly like new cases waiting for a letter and were 3,927 of the "needing
            action" count. Listed apart, oldest first, a page at a time; most are what the Clio
            archive proposal covers.
          */}
          {!needsActionLoading && clioTotal > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Clio cases to review ({clioTotal})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Imported from Clio with no stage and no letter of representation on record. Set a
                stage on any that are still live; most of the rest are covered by the Clio archive.
              </Typography>
              {clioCases.length > 0 &&
                renderActionTable('Oldest first', clioCases, `Showing ${clioCases.length} of ${clioTotal}.`)}
              {clioCases.length < clioTotal && (
                <Button variant="outlined" size="small" onClick={loadMoreClio} disabled={clioLoading}>
                  {clioLoading
                    ? 'Loading...'
                    : clioCases.length === 0
                      ? `Show the first ${Math.min(CLIO_PAGE_SIZE, clioTotal)}`
                      : `Show ${Math.min(CLIO_PAGE_SIZE, clioTotal - clioCases.length)} more`}
                </Button>
              )}
            </Box>
          )}

          {/*
            Waiting on a court: the 90-day silence rule (Lloyd, 2 Oct 2026; was 45 from Aug 19).

            Its own line, beside the stale list below rather than folded into it. The two measure
            different things and both are his: this counts cases the *court* has gone quiet on,
            where the list below is about our own letter never being acknowledged. A case can be in
            one and not the other, so a single merged number would answer neither question.

            The count comes from the server planner, which is the only thing that can see court
            emails, offers and the manual contact stamp. `months=1` rather than the catch-up
            sweep's six: that staging is for Lloyd's one-off batches, and a standing count should
            not be age-gated. A case cannot be 90 days silent while being younger than that.
          */}
          {courtSilenceCount !== null && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                Waiting on a court ({courtSilenceCount})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Open cases the court has not been in touch about for 90 days. While court status
                requests are switched on in Settings, they are emailed automatically each weekday.
              </Typography>
              <Button component={Link} to="/stale-cases" size="small">
                Review them
              </Button>
            </Box>
          )}

          {/* Stale Cases (G.14) — cases that have quietly stalled */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Stale Cases
              {ticketsLoading ? '' : ` (${staleCases.length}${activeTicketsCapped ? '+' : ''})`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cases with no LOR acknowledgment past the follow-up window, acknowledged with no
              upcoming court date, or going cold (opened weeks ago with no LOR activity). Separate
              from the court-silence count above, which is about the court not answering us.
            </Typography>
            {ticketsLoading ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading stale cases...
                </Typography>
              </Paper>
            ) : staleCases.length === 0 ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No stale cases right now.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case #</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Why stale</TableCell>
                      <TableCell>Court Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staleCases.map(({ ct, reason }) => (
                      <TableRow
                        key={ct.ticket.Ticket_guid}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(ct.ticket.Ticket_guid)}
                      >
                        <TableCell>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                            {ct.ticket.Ticket_MatterNumber || ct.ticket.Ticket_Number || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ct.user && clientDisplayName(ct.user)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={STALE_REASON_LABEL[reason]}
                            color="warning"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {ct.ticket.Ticket_CourtDate
                            ? new Date(ct.ticket.Ticket_CourtDate).toLocaleDateString('en-US')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {ticketsLoading ? (
            <>
              <br />
              <Typography variant="h5" className="">Your Active Tickets</Typography>
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading tickets...
                </Typography>
              </Paper>
            </>
          ) : activeTickets.length > 0 && (
            <>
              <br />
              <Typography variant="h5" className="">Your Active Tickets</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket Number</TableCell>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Citation</TableCell>
                      <TableCell>Representation</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Court Date</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTickets.slice(0, 5).map((contextTicket) => (
                      <TableRow
                        key={contextTicket.ticket.Ticket_guid}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(contextTicket.ticket.Ticket_guid)}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {contextTicket.ticket.Ticket_Number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {contextTicket?.user && 
                            `${contextTicket.user.User_FirstName} ${contextTicket.user.User_LastName}`
                          }
                        </TableCell>
                        <TableCell>{contextTicket.ticket.Ticket_Citation}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {contextTicket.ticket.Ticket_Representation || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{renderStageChip(contextTicket)}</TableCell>
                        <TableCell>
                          {contextTicket.ticket.Ticket_CourtDate
                            ? new Date(contextTicket.ticket.Ticket_CourtDate).toLocaleDateString('en-US')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {new Date(contextTicket.ticket.Ticket_CreatedAt).toLocaleDateString('en-US')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <br />
        </Container>
      </div>
    </>
  );
}
export default ManagerHomeView;